import { useEffect, useMemo, useState } from 'react';
import { X, Wifi, Zap, Globe, Monitor } from 'lucide-react';
import { DEFAULT_SMART_HOME_UI, getSmartHomeUi } from '../api';
import { useHaptic } from '../hooks/useHaptic';
import { GlassCard, SectionTitle, StatusPill } from '../components/Glass';
import { mapSmartHomeUiToView, type SmartHomeEcosystemView } from '../lib/smartHomeUi';
import { SCROLL_PAD_BOTTOM } from '../lib/appLayout';

/** Extra scroll pad inside ecosystem sheet so last lines clear the floating tab bar (nav stays visible). */
const SHEET_SCROLL_PAD_BOTTOM = 'calc(var(--app-bottom-nav-offset) + 1.25rem)';

function protocolIcon(id: string) {
  switch (id) {
    case 'zigbee':
      return <Zap size={16} />;
    case 'matter':
      return <Globe size={16} />;
    case 'rs485':
      return <Monitor size={16} />;
    default:
      return <Wifi size={16} />;
  }
}

export default function SmartHomePage() {
  const [activeEco, setActiveEco] = useState<SmartHomeEcosystemView | null>(null);
  const [rawUi, setRawUi] = useState(DEFAULT_SMART_HOME_UI);
  const haptic = useHaptic();

  useEffect(() => {
    getSmartHomeUi()
      .then(setRawUi)
      .catch(() => setRawUi(DEFAULT_SMART_HOME_UI));
  }, []);

  const view = useMemo(() => mapSmartHomeUiToView(rawUi), [rawUi]);

  return (
    <div className="flex flex-col min-h-screen">

      <div className="header-glass px-4 pt-5 pb-4">
        <h1 className="text-xl font-black" style={{ color: 'var(--text-1)' }}>{view.page.title}</h1>
        <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{view.page.subtitle}</p>
      </div>

      <div
        className="flex-1 overflow-y-auto px-4 pt-4 space-y-5"
        style={{ paddingBottom: SCROLL_PAD_BOTTOM }}
      >

        <div>
          <SectionTitle className="mb-3">{view.page.ecosystems_section_title}</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            {view.ecosystems.map((eco) => (
              <button key={eco.id} onClick={() => { haptic.selection(); setActiveEco(eco); }} className="press text-left">
                <GlassCard className="p-4 flex flex-col items-start gap-2 h-full">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${eco.gradient} flex items-center justify-center text-xl`}>
                    {eco.icon}
                  </div>
                  <div>
                    <p className="text-sm font-bold leading-tight" style={{ color: 'var(--text-1)' }}>{eco.name}</p>
                    <p className="text-[11px] mt-0.5 leading-tight" style={{ color: 'var(--text-3)' }}>{eco.tagline}</p>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {eco.protocols.slice(0, 2).map((p) => (
                      <StatusPill key={p} className="text-[9px] px-1.5 py-0.5">{p}</StatusPill>
                    ))}
                  </div>
                </GlassCard>
              </button>
            ))}
          </div>
        </div>

        <div>
          <SectionTitle className="mb-3">{view.page.protocols_section_title}</SectionTitle>
          <div className="space-y-2">
            {view.protocols.map((proto) => (
              <GlassCard
                key={proto.id}
                className="flex items-start gap-3 p-3.5"
                style={{
                  background: 'rgba(10,25,47,0.6)',
                  border: '1px solid var(--border-glass)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: `0 0 20px ${proto.glowColor}`,
                }}
              >
                <div className="mt-0.5 shrink-0" style={{ color: 'var(--cyan)' }}>{protocolIcon(proto.id)}</div>
                <div>
                  <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>{proto.name}</p>
                  <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-2)' }}>{proto.desc}</p>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>

        <GlassCard tone="accent" className="p-4">
          <p className="font-bold text-sm mb-3" style={{ color: 'var(--cyan-bright)' }}>{view.page.chooser_section_title}</p>
          <div className="space-y-2.5">
            {view.chooser.map(({ id, q, a }) => (
              <div key={id} className="flex items-start justify-between gap-3">
                <p className="text-xs leading-snug flex-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{q}</p>
                <span className="text-xs font-bold shrink-0 text-right" style={{ color: 'var(--cyan-bright)' }}>{a}</span>
              </div>
            ))}
          </div>
        </GlassCard>

      </div>

      {activeEco && (
        <div
          className="fixed inset-0 z-40 flex flex-col justify-end"
          style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(12px)' }}
          onClick={() => { haptic.impact('light'); setActiveEco(null); }}
        >
          <div
            className="rounded-t-3xl flex flex-col min-h-0"
            style={{
              height: '88dvh',
              background: 'var(--sapphire-light)',
              border: '1px solid var(--border-glass)',
              borderBottom: 'none',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center gap-3 px-5 pt-5 pb-3 shrink-0"
              style={{ borderBottom: '1px solid var(--border-subtle)' }}
            >
              <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${activeEco.gradient} flex items-center justify-center text-2xl shrink-0`}>
                {activeEco.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-extrabold" style={{ color: 'var(--text-1)' }}>{activeEco.name}</h2>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{activeEco.tagline}</p>
              </div>
              <button
                aria-label="Закрыть детали экосистемы"
                onClick={() => { haptic.selection(); setActiveEco(null); }}
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid var(--border-subtle)' }}
              >
                <X size={16} color="var(--text-2)" />
              </button>
            </div>

            <div
              className="flex-1 overflow-y-auto overscroll-contain px-5 space-y-5 pt-4 min-h-0"
              style={{ paddingBottom: SHEET_SCROLL_PAD_BOTTOM }}
            >
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{activeEco.description}</p>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>◈ Протоколы</p>
                <div className="flex flex-wrap gap-2">
                  {activeEco.protocols.map((p) => (
                    <StatusPill key={p} className="text-xs px-3 py-1 rounded-xl">{p}</StatusPill>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>◈ Как подключить</p>
                <div className="space-y-2.5">
                  {activeEco.howTo.map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5"
                        style={{ background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
                      >
                        {i + 1}
                      </span>
                      <p className="text-sm leading-snug" style={{ color: 'var(--text-2)' }}>{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              {activeEco.requirements && activeEco.requirements.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>◈ Что нужно</p>
                  <div className="space-y-1.5">
                    {activeEco.requirements.map((r, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span style={{ color: '#68D391' }}>✓</span>
                        <p className="text-sm" style={{ color: 'var(--text-2)' }}>{r}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeEco.limitations && activeEco.limitations.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>◈ Ограничения</p>
                  <div className="space-y-1.5">
                    {activeEco.limitations.map((l, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="mt-0.5" style={{ color: '#F6AD55' }}>⚠</span>
                        <p className="text-sm leading-snug" style={{ color: 'var(--text-2)' }}>{l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
